import { createSelector } from "@reduxjs/toolkit";

import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { type RootState } from "@ds/store/bootstrap";
import { selectorsExtractIds, selectorsOutputEntitiesByIds } from "@ds/store/global-selectors";

import { getAggregatedModel } from "@ds/utils/entities";
import { LoadStatus } from "@ds/utils/reducer";

import { CampaignsRemoteOperation, entitySelectors } from "./slice";

const selectBaseState = (state: RootState) => state.content.campaigns;
const selectLoadStatus = (state: RootState, operation = CampaignsRemoteOperation.CRUD) =>
  selectBaseState(state).loadStatusMap[operation];

export const selectCampaignsIsLoading = (state: RootState, operation = CampaignsRemoteOperation.CRUD) =>
  selectLoadStatus(state, operation) === LoadStatus.Loading;

export const selectCampaignsError = (state: RootState) => selectBaseState(state).error;

export const selectTableCampaignIds = (
  state: RootState,
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = MainTableDataTypeEnum.ContentCampaigns,
) => selectBaseState(state).tableIds[tableType];

export const selectSelectedCampaignIds = (
  state: RootState,
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = MainTableDataTypeEnum.ContentCampaigns,
) => selectBaseState(state).selectedIds[tableType];

export const selectCampaignById = (state: RootState, id: number) =>
  entitySelectors.selectById(selectBaseState(state), id) as Campaign | undefined;

export const selectCampaignIds = (state: RootState) => entitySelectors.selectIds(selectBaseState(state));
export const selectCampaigns = (state: RootState) => entitySelectors.selectAll(selectBaseState(state));

export const selectCampaignsByIds = createSelector(
  selectBaseState,
  selectorsExtractIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectTableCampaigns = createSelector(
  selectBaseState,
  selectTableCampaignIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectSelectedCampaigns = createSelector(
  selectBaseState,
  selectSelectedCampaignIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectAggregatedCampaignByIds = createSelector(selectBaseState, selectorsExtractIds, (state, ids) =>
  ids?.length ? getAggregatedModel(selectorsOutputEntitiesByIds(entitySelectors)(state, ids)) : null,
);

export const selectAggregatedCampaignForSelectedEntities = createSelector(selectSelectedCampaigns, selectedCampaigns =>
  getAggregatedModel(selectedCampaigns),
);
